/**
 * Which Ask Lens create-action chips to show for an assistant answer.
 * Prefer server `suggested_actions`; fall back to a light client heuristic
 * for older messages that lack meta.
 */

export const ASK_ACTION_OBSERVATION = 'observation';
export const ASK_ACTION_TASK = 'task';
export const ASK_ACTION_RAID = 'raid';
export const ASK_ACTION_ACTION_LOG = 'action_log';

export const ASK_CREATE_ACTIONS = [
    ASK_ACTION_OBSERVATION,
    ASK_ACTION_TASK,
    ASK_ACTION_RAID,
    ASK_ACTION_ACTION_LOG,
];

const OBS_CUE =
    /\b(observation|findings?|finding|diligence\s+note|gap\s+in|we\s+observed|notable|concern|red\s+flag|strengths?|weaknesses?|opportunit(?:y|ies)|threats?|swot)\b/i;
const TASK_CUE =
    /\b(task|to-?do|action\s+item|next\s+steps?|follow[- ]?up\s+task|should\s+(?:be\s+)?(?:assigned|done|completed)|recommend(?:ed)?\s+that|need\s+to|must\s+ensure|assign(?:ed)?\s+to)\b/i;
const RAID_CUE =
    /\b(raid|risks?|issues?|assumptions?|dependencies|dependency|blocker|mitigation|residual\s+risk|open\s+risk|key\s+risk|contingency|exposure)\b/i;
const ACTION_LOG_CUE =
    /\b(action\s+log|decision\s+log|logged\s+action|actions?\s+taken|decided\s+to|management\s+decision|agreed\s+to|decision\s+was)\b/i;

export function normalizeSuggestedActions(raw) {
    if (!Array.isArray(raw)) return null;
    const out = [];
    for (const item of raw) {
        let key = String(item || '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '_');
        if (key === 'actionlog' || key === 'action-log') key = ASK_ACTION_ACTION_LOG;
        if (ASK_CREATE_ACTIONS.includes(key) && !out.includes(key)) out.push(key);
    }
    return out;
}

export function heuristicSuggestedActions({
    question = '',
    answer = '',
    retrievalDebug = null,
} = {}) {
    const rd = retrievalDebug && typeof retrievalDebug === 'object' ? retrievalDebug : {};
    const intent = String(rd.router_intent || rd.intent || '').toLowerCase();
    if (intent === 'vdr_catalog' || rd.vdr_catalog) return [];

    const text = `${question || ''}\n${answer || ''}`.trim();
    if (text.length < 40) return [];

    const lowerAnswer = String(answer || '').toLowerCase();
    if (
        [
            'insufficient evidence',
            'cannot answer',
            "can't answer",
            'no supporting evidence',
            'unable to verify',
        ].some(p => lowerAnswer.includes(p))
    ) {
        return [];
    }

    const found = [];
    if (OBS_CUE.test(text)) found.push(ASK_ACTION_OBSERVATION);
    if (TASK_CUE.test(text)) found.push(ASK_ACTION_TASK);
    if (RAID_CUE.test(text)) found.push(ASK_ACTION_RAID);
    if (ACTION_LOG_CUE.test(text)) found.push(ASK_ACTION_ACTION_LOG);

    if (
        !found.length &&
        /(^|\n)\s*[-*•]\s+\S.{20,}/m.test(answer || '') &&
        /\b(gap|missing|unclear|incomplete|inconsistent|outdated)\b/i.test(answer || '')
    ) {
        found.push(ASK_ACTION_OBSERVATION);
    }

    return ASK_CREATE_ACTIONS.filter(a => found.includes(a));
}

/** Resolve chips for an assistant message / ask payload. */
export function resolveAskSuggestedActions({
    suggestedActions = null,
    responseActions = null,
    hasResponseActionsField = false,
    question = '',
    answer = '',
    retrievalDebug = null,
    isAbstention = false,
} = {}) {
    if (isAbstention) return [];
    if (responseActions || hasResponseActionsField) return [];
    const fromServer = normalizeSuggestedActions(suggestedActions);
    if (fromServer) return fromServer;
    return heuristicSuggestedActions({ question, answer, retrievalDebug });
}
